import { Component, EventEmitter, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { TableBase } from '../../shared/components/table/table-base';
import { ActionEvent, CellClickEvent, ColumnTypes, SortEvent } from '../../shared/components/table/table.component';
import { StopWord } from '../../shared/models/stop-word.model';
import { ModalService } from '../../shared/services/modal.service';
import { NotificationService } from '../../shared/services/notification.service';
import { StopWordsService } from '../../shared/services/stop-words.service';
import { catchError, throwError } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-stop-words-table',
  templateUrl: './stop-words-table.component.html',
  styleUrls: ['./stop-words-table.component.scss'],
})
export class StopWordsTableComponent extends TableBase implements OnInit {
  @Output() actions = new EventEmitter<ActionEvent>();
  @Output() onAfterDelete = new EventEmitter<void>();

  @ViewChild('deleteModalTpl', { read: TemplateRef, static: false }) deleteModalTpl: any;
  @ViewChild('titleModalTpl', { read: TemplateRef, static: false }) titleModalTpl: any;

  isAdmin = false;
  currentTitle = '';
  storageContextName = 'stop-words-table';

  constructor(
    public notificationService: NotificationService,
    public service: StopWordsService,
    public modal: ModalService
  ) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.tableActions = [
      { icon: 'fa fa-trash text-danger', name: 'delete' },
    ];
    this.setColumns(
      this.createColumns([
        { title: 'Content', prop: 'content', cellClassName: 'editable', sort: false },
        { title: 'LNT', prop: 'lnt', type: ColumnTypes.INPUT_SWITCH, cellClassName: 'text-center', sort: false },
        { title: 'MNT', prop: 'mnt', type: ColumnTypes.INPUT_SWITCH, cellClassName: 'text-center', sort: false },
        { title: 'MO', prop: 'mo', type: ColumnTypes.INPUT_SWITCH, cellClassName: 'text-center', sort: false },
      ])
    );
    this.update();
  }

  onPageChange(event) {
    this.page = event;
    this.update();
  }

  onSort(order: SortEvent[]) {
    this.onChangeOrder(order);
    this.order = order;
    this.update();
  }

  update() {
    this.loading = true;
    this.service.all().subscribe({
      next: collection => {
        this.setData(this.createRows(collection), collection.length);
        this.loading = false;
      },
      error: e => {
        this.loading = false;
        this.notificationService.error({
          title: 'Stop words',
          message: 'An error occurred while loading the contents',
          serviceName: 'API-GATEWAY',
          requestMessage: e.statusText,
          requestCode: e.status,
          ts: e.timestamp ? e.timestamp : null,
        });
      },
    });
  }

  onCellClick(event: CellClickEvent) {
    if (event.column.prop === 'content') {
      this.actions.emit({ name: 'edit', row: event.row, column: event.column });
    }
  }

  onAction(event: ActionEvent) {
    this.actions.emit(event);
    if (event.name === 'switch') {
      const stopWord: StopWord = event.row.data;
      const prop = event.column.prop;
      if (['lnt', 'mnt', 'mo'].includes(prop)) {
        this.loading = true;
        this.service.updateFlags(stopWord).pipe(
          catchError(e => {
            stopWord[prop] = !stopWord[prop];
            this.notificationService.error({
              title: 'Stop words',
              message: 'An error occurred while updating the content',
              serviceName: 'API-GATEWAY',
              requestMessage: e.statusText,
              requestCode: e.status,
              ts: e.timestamp ? e.timestamp : null,
            });
            return throwError(() => e);
          })
        ).subscribe(s => {
          this.notificationService.success('Content updated', 'Stop words');
          this.update();
        });
      }
    }
    if (event.name === 'delete') {
      let dialogRef = this.modal.alert().size('sm').component(this.deleteModalTpl).open();
      dialogRef.result.then(result => {
        if (result) {
          this.delete(event.row.data);
        }
      });
    }
  }

  delete(word: StopWord) {
    this.loading = true;
    this.service.delete(word).pipe(
      catchError(e => {
        this.loading = false;
        return throwError(() => e);
      })
    ).subscribe(() => {
      this.notificationService.success('Content removed', 'Stop words');
      this.onAfterDelete.emit();
      this.update();
    });
  }

  changeSize($event, size) {
    super.onChangeSize(size);
    this.update();
  }
}
